import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, Image as ImageIcon, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  uploading: boolean;
  type: 'file' | 'image';
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onFileSelect,
  uploading,
  type
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const acceptedTypes = type === 'image' 
    ? 'image/*' 
    : '.pdf,.doc,.docx,.txt,.zip,.rar';

  const maxSize = type === 'image' ? '10MB' : '50MB';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md glass border-border/30">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {type === 'image' ? (
              <ImageIcon className="w-5 h-5 text-primary" />
            ) : (
              <FileText className="w-5 h-5 text-primary" />
            )}
            <span>
              {type === 'image' ? 'Carica Immagine' : 'Carica File'}
            </span>
          </DialogTitle>
          <DialogDescription>
            Seleziona un {type === 'image' ? 'immagine' : 'file'} da caricare (max {maxSize})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              "border-border/50 hover:border-primary/50",
              "glass hover:bg-primary/5"
            )}
          >
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 rounded-full glass border border-border/30 flex items-center justify-center">
                <Upload className="w-8 h-8 text-primary" />
              </div>
              
              <div className="space-y-2">
                <p className="font-medium text-foreground">
                  Trascina il file qui o clicca per selezionare
                </p>
                <p className="text-sm text-muted-foreground">
                  {type === 'image' 
                    ? 'JPG, PNG, GIF fino a 10MB'
                    : 'PDF, DOC, TXT, ZIP fino a 50MB'
                  }
                </p>
              </div>
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gradient-primary"
              >
                Seleziona File
              </Button>
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={uploading}
            >
              Annulla
            </Button>
          </div>
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-sm font-medium">Caricamento...</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FileUploadModal;